import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RefreshCw, Trophy, GraduationCap } from "lucide-react";
import { api } from "../lib/api";
import PushSubscribeButton from "./PushSubscribeButton";

/** AI-powered "For you" tile — Gemini-generated event picks based on the
 *  player's profile + upcoming events. Lazily loaded; silent fail if the
 *  backend has no API key or the model returns an empty array. */
export default function AiRecommendations() {
  const [state, setState] = useState({ items: [], loading: true, generatedAt: null });

  const load = async (refresh = false) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = refresh
        ? await api.post("/me/recommendations/refresh")
        : await api.get("/me/recommendations");
      setState({ items: r.data.items || [], loading: false, generatedAt: r.data.generated_at });
    } catch {
      setState({ items: [], loading: false, generatedAt: null });
    }
  };

  useEffect(() => { load(false); }, []);

  // Silent render nothing when there's nothing to surface — avoids a sad empty state.
  if (!state.loading && state.items.length === 0) return null;

  return (
    <div className="pp-card-elevated p-6" data-testid="ai-recs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
          <Sparkles size={18} className="text-[#FFD60A]" /> For you
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <PushSubscribeButton />
          <button
            onClick={() => load(true)}
            disabled={state.loading}
            data-testid="ai-recs-refresh"
            className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
            title="Regenerate picks"
          >
            <RefreshCw size={12} className={state.loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {state.loading && state.items.length === 0
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="pp-card p-4 animate-pulse h-28" />
            ))
          : state.items.map((it) => (
              <Link
                key={it.id}
                to={`/${it.kind === "tournament" ? "tournaments" : "lessons"}/${it.id}`}
                data-testid={`ai-rec-${it.id}`}
                className="pp-card p-4 block hover:border-[#FFD60A]/40 transition-colors"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                  {it.kind === "tournament" ? <Trophy size={12} /> : <GraduationCap size={12} />} {it.kind}
                </div>
                <div className="font-semibold mt-2 line-clamp-2">{it.headline}</div>
                <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{it.why}</div>
              </Link>
            ))}
      </div>
    </div>
  );
}
